import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, updateDoc, doc, Timestamp, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { uploadListingPDF, createListingFromPDF, createListingManually } from '../services/listingService';
import { Listing, Offer } from '../types';
import OfferCompareModal from './OfferCompareModal';
import './Listings.css';

const Listings: React.FC = () => {
  const [listings, setListings] = useState<Listing[]>([]);
  const [offersByListing, setOffersByListing] = useState<{ [listingId: string]: Offer[] }>({});
  const [expandedListings, setExpandedListings] = useState<Set<string>>(new Set());
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addMode, setAddMode] = useState<'pdf' | 'manual'>('pdf');

  const [listingPDF, setListingPDF] = useState<File | null>(null);
  const [isUploadingListing, setIsUploadingListing] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isDraggingListing, setIsDraggingListing] = useState(false);
  const [manualFields, setManualFields] = useState({
    address: '', city: '', state: '', zipCode: '',
    mlsNumber: '', listingPrice: '', listingDate: '',
  });
  const [showManualErrors, setShowManualErrors] = useState(false);
  const [processingListingId, setProcessingListingId] = useState<string | null>(null);
  const [filterDate, setFilterDate] = useState('');
  const [filterProperty, setFilterProperty] = useState('');
  const [filterAgent, setFilterAgent] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  // Compare state: tracks selected offer IDs per listing and which listing's modal is open
  const [selectedOffersByListing, setSelectedOffersByListing] = useState<{ [listingId: string]: Set<string> }>({});
  const [compareListingId, setCompareListingId] = useState<string | null>(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) return;

    const fetchListingsAndOffers = async () => {
      try {
        setLoading(true);

        // Fetch user's listings
        const listingsQuery = query(
          collection(db, 'listings'),
          where('userId', '==', currentUser.uid)
        );
        const listingsSnapshot = await getDocs(listingsQuery);
        const listingsData: Listing[] = [];
        listingsSnapshot.forEach((doc) => {
          const data = doc.data();
          // Filter out temp/failed listings with pending extraction address
          if (data.address === 'Pending extraction from purchase agreement') {
            return;
          }
          listingsData.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate()
          } as Listing);
        });
        setListings(listingsData);

        // Fetch all offers for user's listings
        const offersQuery = query(
          collection(db, 'offers'),
          where('userId', '==', currentUser.uid)
        );
        const offersSnapshot = await getDocs(offersQuery);
        const offersByListingMap: { [listingId: string]: Offer[] } = {};
        
        offersSnapshot.forEach((doc) => {
          const offer = {
            id: doc.id,
            ...doc.data(),
            submissionDate: doc.data().submissionDate?.toDate()
          } as Offer;
          
          // Only include successfully parsed offers (exclude pending and failed)
          if (offer.listingId && offer.parsingStatus === 'completed') {
            if (!offersByListingMap[offer.listingId]) {
              offersByListingMap[offer.listingId] = [];
            }
            offersByListingMap[offer.listingId].push(offer);
          }
        });
        
        // Sort offers by submission date (newest first)
        Object.keys(offersByListingMap).forEach(listingId => {
          offersByListingMap[listingId].sort((a, b) => {
            const dateA = a.submissionDate?.getTime() || 0;
            const dateB = b.submissionDate?.getTime() || 0;
            return dateB - dateA;
          });
        });
        
        setOffersByListing(offersByListingMap);

      } catch (error) {
        console.error('Error fetching listings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchListingsAndOffers();
  }, [currentUser]);

  const validateListingFile = (file: File): string | null => {
    if (file.type !== 'application/pdf') return 'Only PDF files are allowed';
    if (file.size > 10 * 1024 * 1024) return 'File must be less than 10MB';
    return null;
  };

  const handleListingPDFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const err = validateListingFile(e.target.files[0]);
      if (err) { setUploadError(err); return; }
      setListingPDF(e.target.files[0]);
      setUploadError('');
    }
  };

  const handleListingDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleListingDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingListing(true);
  };

  const handleListingDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingListing(false);
  };

  const handleListingDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingListing(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    const err = validateListingFile(file);
    if (err) { setUploadError(err); return; }
    setListingPDF(file);
    setUploadError('');
  };

  const handleAddListingManually = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    const hasErrors = !manualFields.address.trim() || !manualFields.city.trim() || !manualFields.state.trim() ||
      !manualFields.zipCode.trim() || !manualFields.mlsNumber.trim() ||
      !manualFields.listingPrice || !manualFields.listingDate;
    if (hasErrors) { setShowManualErrors(true); return; }
    setIsUploadingListing(true);
    setUploadError('');
    try {
      const listingId = await createListingManually(currentUser.uid, {
        address: manualFields.address.trim(),
        city: manualFields.city.trim() || undefined,
        state: manualFields.state.trim() || undefined,
        zipCode: manualFields.zipCode.trim() || undefined,
        mlsNumber: manualFields.mlsNumber.trim() || undefined,
        listingPrice: manualFields.listingPrice ? parseFloat(manualFields.listingPrice) : null,
        listingDate: manualFields.listingDate || null,
      });
      const newListing: Listing = {
        id: listingId,
        userId: currentUser.uid,
        address: manualFields.address.trim(),
        city: manualFields.city.trim() || undefined,
        state: manualFields.state.trim() || undefined,
        zipCode: manualFields.zipCode.trim() || undefined,
        mlsNumber: manualFields.mlsNumber.trim() || null,
        listingPrice: manualFields.listingPrice ? parseFloat(manualFields.listingPrice) : null,
        listingDate: manualFields.listingDate || null,
        status: 'active',
        parsingStatus: 'completed',
        createdAt: new Date(),
      };
      setListings(prev => [newListing, ...prev]);
      setOffersByListing(prev => ({ ...prev, [listingId]: [] }));
      setManualFields({ address: '', city: '', state: '', zipCode: '', mlsNumber: '', listingPrice: '', listingDate: '' });
      setShowManualErrors(false);
      setShowAddForm(false);
    } catch (err) {
      console.error('Error creating listing:', err);
      setUploadError('Failed to create listing. Please try again.');
    } finally {
      setIsUploadingListing(false);
    }
  };

  const handleAddListing = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    if (!listingPDF) {
      setUploadError('Please select an MLS listing PDF');
      return;
    }

    setIsUploadingListing(true);
    setUploadError('');

    try {
      // Upload PDF to storage
      const { fileName } = await uploadListingPDF(listingPDF, currentUser.uid);

      // Create listing document (triggers Cloud Function)
      const listingId = await createListingFromPDF(currentUser.uid, fileName, '');

      // Add temporary listing to local state
      const tempListing: Listing = {
        id: listingId,
        userId: currentUser.uid,
        address: 'Processing MLS listing...',
        status: 'pending',
        parsingStatus: 'processing',
        createdAt: new Date()
      };
      setListings(prev => [tempListing, ...prev]);
      setOffersByListing(prev => ({ ...prev, [listingId]: [] }));
      setProcessingListingId(listingId);

      // Reset form
      setListingPDF(null);
      setShowAddForm(false);
      const fileInput = document.getElementById('listing-pdf-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

      // Real-time listener: update listing card when Cloud Function finishes
      const unsubscribe = onSnapshot(doc(db, 'listings', listingId), (snapshot) => {
        const data = snapshot.data();
        if (!data) return;

        if (data.parsingStatus === 'completed' || data.parsingStatus === 'failed') {
          setListings(prev => prev.map(l =>
            l.id === listingId
              ? { id: listingId, ...data, createdAt: data.createdAt?.toDate(), updatedAt: data.updatedAt?.toDate() } as Listing
              : l
          ));
          setProcessingListingId(null);
          unsubscribe();
        }
      });
    } catch (error) {
      console.error('Error uploading listing:', error);
      setUploadError('Failed to upload listing PDF. Please try again.');
    } finally {
      setIsUploadingListing(false);
    }
  };

  const handleStatusChange = async (listingId: string, newStatus: string) => {
    try {
      const listingRef = doc(db, 'listings', listingId);
      await updateDoc(listingRef, {
        status: newStatus,
        updatedAt: Timestamp.now()
      });

      // Update local state
      setListings(listings.map(listing => 
        listing.id === listingId 
          ? { ...listing, status: newStatus as Listing['status'], updatedAt: new Date() }
          : listing
      ));
    } catch (error) {
      console.error('Error updating listing status:', error);
      alert('Failed to update status. Please try again.');
    }
  };

  const toggleListingExpanded = (listingId: string) => {
    const newExpanded = new Set(expandedListings);
    if (newExpanded.has(listingId)) {
      newExpanded.delete(listingId);
    } else {
      newExpanded.add(listingId);
    }
    setExpandedListings(newExpanded);
  };

  const getScoreBadgeClass = (score: number | undefined) => {
    if (score === undefined || score === null) return 'score-badge score-pending';
    // Higher PTC scores are better
    if (score >= 70) return 'score-badge score-excellent'; // Green for high scores
    if (score >= 50) return 'score-badge score-fair'; // Yellow for medium scores
    return 'score-badge score-poor'; // Red for low scores
  };

  const formatScore = (score: number | undefined) => {
    if (score === undefined || score === null) return 'Pending';
    return `${Math.round(score)}`;
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active': return 'status-badge status-active';
      case 'pending': return 'status-badge status-pending';
      case 'sold': return 'status-badge status-sold';
      case 'cancelled': return 'status-badge status-cancelled';
      default: return 'status-badge';
    }
  };

  if (loading) {
    return (
      <div className="listings-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading listings...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="listings-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading listings...</p>
        </div>
      </div>
    );
  }
  return (
    <div className="listings-container">
      <div className="listings-sticky-header">
        <div className="listings-header">
          <div>
            <h2>My Property Listings</h2>
            <p>Manage your property listings and track submitted offers</p>
          </div>
          <div className="listings-header-actions">
            <button
              className="add-listing-button"
              onClick={() => setShowAddForm(!showAddForm)}
            >
              {showAddForm ? '✕ Cancel' : '+ Add New Listing'}
            </button>
            <button
              className="submit-offer-button"
              onClick={() => navigate('/submit-offer')}
            >
              + Submit Offer
            </button>
          </div>
        </div>

        {listings.length > 0 && (
          <div className="filters-section" style={{ marginBottom: '0', display: 'flex', gap: '15px', flexWrap: 'wrap', padding: '15px', background: '#f5f5f5', borderRadius: '8px' }}>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>Filter by Date</label>
              <input
                type="date"
                value={filterDate}
                onChange={(e) => {
                  setFilterDate(e.target.value);
                  setCurrentPage(1);
                }}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>Filter by Property</label>
              <input
                type="text"
                placeholder="Search address..."
                value={filterProperty}
                onChange={(e) => {
                  setFilterProperty(e.target.value);
                  setCurrentPage(1);
                }}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>Filter by Agent Email</label>
              <input
                type="text"
                placeholder="Search agent email..."
                value={filterAgent}
                onChange={(e) => {
                  setFilterAgent(e.target.value);
                  setCurrentPage(1);
                }}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
            {(filterDate || filterProperty || filterAgent) && (
              <button
                onClick={() => {
                  setFilterDate('');
                  setFilterProperty('');
                  setFilterAgent('');
                  setCurrentPage(1);
                }}
                style={{ alignSelf: 'flex-end', padding: '8px 16px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Clear Filters
              </button>
            )}
          </div>
        )}
      </div>{/* end listings-sticky-header */}

      {showAddForm && (
        <div className="add-listing-form-card">
          <h3>Add New Property Listing</h3>

          {/* Mode tabs */}
          <div className="add-listing-tabs">
            <button
              className={`add-listing-tab${addMode === 'pdf' ? ' active' : ''}`}
              onClick={() => { setAddMode('pdf'); setUploadError(''); }}
              type="button"
            >
              📄 Upload MLS PDF
            </button>
            <button
              className={`add-listing-tab${addMode === 'manual' ? ' active' : ''}`}
              onClick={() => { setAddMode('manual'); setUploadError(''); }}
              type="button"
            >
              ✏️ Enter Manually
            </button>
          </div>

          {uploadError && (
            <div className="error-message" style={{ margin: '12px 0', padding: '12px', backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: '6px' }}>
              {uploadError}
            </div>
          )}

          {addMode === 'pdf' ? (
            <>
              <p className="upload-description">
                Upload an MLS listing page PDF. Our system will automatically extract property details including
                MLS#, Address, Listing Price, Property Type, Bedrooms, Bathrooms, Square Footage, and more.
              </p>
              <form onSubmit={handleAddListing} className="add-listing-form">
                <div
                  className={`upload-section drop-zone${isDraggingListing ? ' drop-zone-active' : ''}`}
                  onDragOver={handleListingDragOver}
                  onDragEnter={handleListingDragEnter}
                  onDragLeave={handleListingDragLeave}
                  onDrop={handleListingDrop}
                  onClick={() => !isUploadingListing && document.getElementById('listing-pdf-input')?.click()}
                >
                  <div className="upload-icon">📄</div>
                  <div className="upload-text">
                    <span className="upload-title">
                      {isDraggingListing ? 'Drop PDF here' : 'Drag & drop MLS listing PDF here'}
                    </span>
                    <span className="upload-subtitle">or click to browse · PDF only · max 10MB</span>
                  </div>
                  <input
                    type="file"
                    id="listing-pdf-input"
                    accept=".pdf,application/pdf"
                    onChange={handleListingPDFChange}
                    disabled={isUploadingListing}
                    className="file-input"
                    style={{ display: 'none' }}
                  />
                </div>

                {listingPDF && (
                  <div className="selected-files">
                    <div className="file-item">
                      <div className="file-info">
                        <span className="file-icon">📄</span>
                        <div className="file-details">
                          <span className="file-name">{listingPDF.name}</span>
                          <span className="file-size">{(listingPDF.size / 1024 / 1024).toFixed(2)} MB</span>
                        </div>
                      </div>
                      {!isUploadingListing && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setListingPDF(null);
                            const input = document.getElementById('listing-pdf-input') as HTMLInputElement;
                            if (input) input.value = '';
                          }}
                          className="remove-button"
                          title="Remove file"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <div className="form-actions">
                  <button type="button" onClick={() => { setShowAddForm(false); setListingPDF(null); setUploadError(''); }} className="cancel-btn" disabled={isUploadingListing}>
                    Cancel
                  </button>
                  <button type="submit" className="submit-btn" disabled={isUploadingListing || !listingPDF}>
                    {isUploadingListing ? 'Uploading...' : 'Upload & Process Listing'}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              <p className="upload-description">
                Enter the key listing details manually. You can always update additional fields from the listing detail page.
              </p>
              <form onSubmit={handleAddListingManually} className="add-listing-form">
                <div className="manual-form-grid">
                  <div className="manual-form-field full-width">
                    <label>Street Address <span className="required">*</span></label>
                    <input
                      type="text"
                      placeholder="e.g. 123 Main St"
                      value={manualFields.address}
                      onChange={e => setManualFields(v => ({ ...v, address: e.target.value }))}
                      disabled={isUploadingListing}
                      className={showManualErrors && !manualFields.address.trim() ? 'input-field-error' : ''}
                    />
                  </div>
                  <div className="manual-form-field">
                    <label>City <span className="required">*</span></label>
                    <input
                      type="text"
                      placeholder="e.g. Detroit"
                      value={manualFields.city}
                      onChange={e => setManualFields(v => ({ ...v, city: e.target.value }))}
                      disabled={isUploadingListing}
                      className={showManualErrors && !manualFields.city.trim() ? 'input-field-error' : ''}
                    />
                  </div>
                  <div className="manual-form-field">
                    <label>State <span className="required">*</span></label>
                    <input
                      type="text"
                      placeholder="e.g. MI"
                      value={manualFields.state}
                      onChange={e => setManualFields(v => ({ ...v, state: e.target.value }))}
                      disabled={isUploadingListing}
                      maxLength={2}
                      className={showManualErrors && !manualFields.state.trim() ? 'input-field-error' : ''}
                    />
                  </div>
                  <div className="manual-form-field">
                    <label>Zip Code <span className="required">*</span></label>
                    <input
                      type="text"
                      placeholder="e.g. 48201"
                      value={manualFields.zipCode}
                      onChange={e => setManualFields(v => ({ ...v, zipCode: e.target.value }))}
                      disabled={isUploadingListing}
                      className={showManualErrors && !manualFields.zipCode.trim() ? 'input-field-error' : ''}
                    />
                  </div>
                  <div className="manual-form-field">
                    <label>MLS # <span className="required">*</span></label>
                    <input
                      type="text"
                      placeholder="e.g. 20240012"
                      value={manualFields.mlsNumber}
                      onChange={e => setManualFields(v => ({ ...v, mlsNumber: e.target.value }))}
                      disabled={isUploadingListing}
                      className={showManualErrors && !manualFields.mlsNumber.trim() ? 'input-field-error' : ''}
                    />
                  </div>
                  <div className="manual-form-field">
                    <label>Listing Price (LP) <span className="required">*</span></label>
                    <input
                      type="number"
                      placeholder="e.g. 450000"
                      value={manualFields.listingPrice}
                      onChange={e => setManualFields(v => ({ ...v, listingPrice: e.target.value }))}
                      disabled={isUploadingListing}
                      min={0}
                      className={showManualErrors && !manualFields.listingPrice ? 'input-field-error' : ''}
                    />
                  </div>
                  <div className="manual-form-field">
                    <label>Listing Date <span className="required">*</span></label>
                    <input
                      type="date"
                      value={manualFields.listingDate}
                      onChange={e => setManualFields(v => ({ ...v, listingDate: e.target.value }))}
                      disabled={isUploadingListing}
                      className={showManualErrors && !manualFields.listingDate ? 'input-field-error' : ''}
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    onClick={() => { setShowAddForm(false); setManualFields({ address: '', city: '', state: '', zipCode: '', mlsNumber: '', listingPrice: '', listingDate: '' }); setShowManualErrors(false); setUploadError(''); }}
                    className="cancel-btn"
                    disabled={isUploadingListing}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="submit-btn" disabled={isUploadingListing}>
                    {isUploadingListing ? 'Creating...' : 'Create Listing'}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      )}

      {listings.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🏠</div>
          <h3>No Listings Yet</h3>
          <p>Add your first property listing to start tracking offers</p>
          <button 
            className="primary-button"
            onClick={() => setShowAddForm(true)}
          >
            Add Your First Listing
          </button>
        </div>
      ) : (
          <div className="listings-list">
          {(() => {
            // Apply filters
            let filteredListings = listings.filter(listing => {
              const dateMatch = !filterDate || (listing.createdAt && 
                new Date(listing.createdAt).toISOString().split('T')[0] === filterDate);
              const propertyMatch = !filterProperty || 
                listing.address?.toLowerCase().includes(filterProperty.toLowerCase()) ||
                listing.city?.toLowerCase().includes(filterProperty.toLowerCase()) ||
                listing.zipCode?.includes(filterProperty);
              const agentMatch = !filterAgent || 
                currentUser?.email?.toLowerCase().includes(filterAgent.toLowerCase());
              return dateMatch && propertyMatch && agentMatch;
            });

            // Calculate pagination
            const startIndex = (currentPage - 1) * itemsPerPage;
            const endIndex = startIndex + itemsPerPage;
            const paginatedListings = filteredListings.slice(startIndex, endIndex);

            return (
              <>
                {paginatedListings.map((listing) => {
                  const offers = offersByListing[listing.id || ''] || [];
            const isExpanded = expandedListings.has(listing.id || '');
            
            return (
              <div key={listing.id} className="listing-card-expanded">
                <div 
                  className="listing-header-row"
                  onClick={() => toggleListingExpanded(listing.id || '')}
                >
                  <div className="listing-main-info">
                    <div className="listing-icon-large">🏠</div>
                    <div className="listing-details">
                      <h3 className="listing-address" style={{ fontSize: '16px' }}>
                        {listing.address}
                        {listing.mlsNumber && (
                          <span className="listing-mls-badge">MLS# {listing.mlsNumber}</span>
                        )}
                        {processingListingId === listing.id && (
                          <span style={{ marginLeft: '10px', fontSize: '13px', color: '#6c757d', fontWeight: 'normal' }}>
                            ⋯ Extracting listing details...
                          </span>
                        )}
                      </h3>
                      <p className="listing-location">
                        {[listing.city, listing.state, listing.zipCode].filter(Boolean).join(', ')}
                      </p>
                      {(listing.listingPrice || listing.listingDate) && (
                        <div className="listing-meta-chips">
                          {listing.listingPrice && (
                            <span className="listing-chip">
                              <span className="chip-label">LP</span>
                              ${listing.listingPrice.toLocaleString()}
                            </span>
                          )}
                          {listing.listingDate && (
                            <span className="listing-chip">
                              <span className="chip-label">Listed</span>
                              {new Date(listing.listingDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          )}
                        </div>
                      )}
                      <span className="listing-date">
                        Created {listing.createdAt?.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>

                  <div className="listing-stats-row">
                    <div className="stat-box">
                      <span className="stat-label">Total Offers</span>
                      <span className="stat-number">{offers.length}</span>
                    </div>
                    <div className="stat-box status-dropdown-wrapper">
                      <select
                        value={listing.status}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleStatusChange(listing.id!, e.target.value);
                        }}
                        className={getStatusBadgeClass(listing.status)}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="pending">Pending</option>
                        <option value="active">Active</option>
                        <option value="sold">Sold</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                    <button
                      className="view-listing-details-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/listings/${listing.id}`);
                      }}
                      title="View listing details"
                    >
                      ⓘ View Details
                    </button>
                    <button
                      className="submit-offer-btn-compact"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/submit-offer/${listing.id}`);
                      }}
                      title="Submit an offer for this listing"
                    >
                      + Submit Offer
                    </button>
                    <div className="expand-icon">
                      {isExpanded ? '▼' : '▶'}
                    </div>
                  </div>
                </div>

                {/* Shareable Link Section - Hidden for now since email submission is implemented */}
                {/* <div className="shareable-link-section">
                  {publicListings[listing.id || ''] ? (
                    <div className="link-exists">
                      <div className="link-info">
                        <div className="link-status-badge">
                          {publicListings[listing.id!].isActive ? '🟢 Active' : '🔴 Inactive'}
                        </div>
                        <span className="link-submissions">
                          {publicListings[listing.id!].submissionCount || 0} submission(s) via link
                        </span>
                      </div>
                      <div className="link-actions">
                        {publicListings[listing.id!].isActive && (listing.status === 'active' || listing.status === 'pending') && (
                          <button
                            className="copy-link-btn"
                            onClick={() => handleGenerateLink(listing)}
                          >
                            {copiedLinkId === listing.id ? '✓ Copied!' : '📋 Copy Link'}
                          </button>
                        )}
                        <button
                          className="toggle-link-btn"
                          onClick={() => togglePublicLinkStatus(listing)}
                        >
                          {publicListings[listing.id!].isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      className="generate-link-btn"
                      onClick={() => handleGenerateLink(listing)}
                      title="Create a unique link that buyer agents can use to submit offers for this property. Share this link with potential buyers' agents to collect and compare offers in one place."
                    >
                      🔗 Generate Shareable Link
                    </button>
                  )}
                </div> */}

                {isExpanded && (
                  <div className="offers-section">
                    {offers.length === 0 ? (
                      <div className="no-offers">
                        <p>No offers submitted for this listing yet</p>
                      </div>
                    ) : (
                      <div className="offers-list">
                        <div className="offers-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <h4>Submitted Offers ({offers.length})</h4>
                          {(() => {
                            const sel = selectedOffersByListing[listing.id!] || new Set<string>();
                            return sel.size >= 2 ? (
                              <button
                                className="listing-compare-btn"
                                onClick={e => { e.stopPropagation(); setCompareListingId(listing.id!); }}
                                disabled={sel.size > 4}
                                title={sel.size > 4 ? 'Select up to 4 offers' : undefined}
                              >
                                Compare ({sel.size})
                              </button>
                            ) : sel.size === 1 ? (
                              <span style={{ fontSize: '12px', color: '#6b7280' }}>Select 1 more to compare</span>
                            ) : null;
                          })()}
                        </div>
                        {offers.map((offer) => {
                          // v2 schema (extractedData) with v1 fallback (combinedOfferData)
                          const extracted = offer.extractedData;
                          const preApprovalV1 = offer.combinedOfferData?.preApproval;
                          const purchaseAgreementV1 = offer.combinedOfferData?.purchaseAgreement;
                          const ptcScorePercent = offer.ptcScore ? Math.round(offer.ptcScore * 10) / 10 : undefined;

                          // Field resolution: v2 first, v1 fallback
                          const purchasePrice = extracted?.transaction?.final_sales_price
                            ?? purchaseAgreementV1?.financial?.purchasePrice
                            ?? preApprovalV1?.loan?.purchasePrice;
                          const loanAmount = extracted?.financing?.mortgage_amount
                            ?? preApprovalV1?.loan?.loanAmount;
                          const loanType = extracted?.financing?.mortgage_type
                            || preApprovalV1?.loan?.loanType;
                          const closingDate = extracted?.transaction?.closing_date
                            || purchaseAgreementV1?.contingencies?.closingDate;
                          const lenderName = extracted?.pre_approval?.lender_name
                            || preApprovalV1?.lender?.name;
                          const lenderNmls = extracted?.pre_approval?.loan_officer_nmls
                            || preApprovalV1?.lender?.nmlsId;
                          const agentName = extracted?.agents?.buyer_broker?.agent_name
                            || purchaseAgreementV1?.buyerAgent?.agentName
                            || purchaseAgreementV1?.buyer?.agentName;
                          const agentId = extracted?.agents?.buyer_broker?.agent_id
                            || purchaseAgreementV1?.buyerAgent?.agentId
                            || purchaseAgreementV1?.buyer?.agentId;

                          // Calculate derived values
                          let downPayment = extracted?.financing?.down_payment_amount
                            ?? preApprovalV1?.loan?.downPayment;
                          let ltvRatio: number | undefined = undefined;
                          if (!downPayment && purchasePrice && loanAmount) {
                            downPayment = purchasePrice - loanAmount;
                          }
                          if (loanAmount && purchasePrice && purchasePrice > 0) {
                            ltvRatio = Math.round((loanAmount / purchasePrice) * 100);
                          } else if (preApprovalV1?.loan?.loanToValue) {
                            ltvRatio = preApprovalV1.loan.loanToValue;
                          }

                          const isOfferSelected = (selectedOffersByListing[listing.id!] || new Set()).has(offer.id!);
                          return (
                          <div key={offer.id} className={`offer-row${isOfferSelected ? ' offer-row-selected-listing' : ''}`}>
                            <input
                              type="checkbox"
                              className="offer-row-checkbox"
                              checked={isOfferSelected}
                              onClick={e => e.stopPropagation()}
                              onChange={e => {
                                setSelectedOffersByListing(prev => {
                                  const next = { ...prev };
                                  const sel = new Set(next[listing.id!] || []);
                                  e.target.checked ? sel.add(offer.id!) : sel.delete(offer.id!);
                                  next[listing.id!] = sel;
                                  return next;
                                });
                              }}
                            />
                            <div className="offer-info">
                              <div className="offer-header-row">
                                {agentName && (
                                  <div className="offer-party">
                                    <span className="party-label">Agent</span>
                                    <span className="party-name">{agentName}</span>
                                    {agentId && <span className="party-badge">Lic# {agentId}</span>}
                                  </div>
                                )}
                                <div className="offer-party">
                                  <span className="party-label">Lender</span>
                                  <span className="party-name">{lenderName || 'Unknown'}</span>
                                  {lenderNmls && (
                                    <span className="party-badge nmls">NMLS# {lenderNmls}</span>
                                  )}
                                </div>
                              </div>
                              <div className="offer-loan-details">
                                {purchasePrice && (
                                  <span className="loan-detail">
                                    <strong>Purchase:</strong> ${purchasePrice.toLocaleString()}
                                  </span>
                                )}
                                {loanAmount && (
                                  <span className="loan-detail">
                                    <strong>Loan:</strong> ${loanAmount.toLocaleString()}
                                  </span>
                                )}
                                {downPayment && downPayment > 0 && (
                                  <span className="loan-detail">
                                    <strong>Down:</strong> ${downPayment.toLocaleString()}
                                  </span>
                                )}
                                {ltvRatio && (
                                  <span className="loan-detail">
                                    <strong>LTV:</strong> {ltvRatio}%
                                  </span>
                                )}
                                {loanType && (
                                  <span className="loan-detail">
                                    <strong>Type:</strong> {loanType}
                                  </span>
                                )}
                                {closingDate && (
                                  <span className="loan-detail">
                                    <strong>Closing:</strong> {new Date(closingDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  </span>
                                )}
                              </div>
                              <div className="offer-meta">
                                <span>Submitted {offer.submissionDate?.toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}</span>
                              </div>
                            </div>
                            <div className="offer-score-section">
                              <div 
                                className={getScoreBadgeClass(ptcScorePercent)}
                                title={ptcScorePercent !== undefined
                                  ? `PTC Score: ${ptcScorePercent} - ${offer.ptcInterpretation || 'Score calculated'}`
                                  : 'Score calculation pending'}
                              >
                                {formatScore(ptcScorePercent)}
                              </div>
                              <div className="offer-actions">
                                {offer.ptcRecommendation && (
                                  <button 
                                    className="recommendations-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedOffer(offer);
                                      setShowRecommendations(true);
                                    }}
                                    title={offer.ptcRecommendation}
                                  >
                                    💡 View Recommendation
                                  </button>
                                )}
                                <button 
                                  className="view-details-btn"
                                  onClick={() => navigate(`/offers/${offer.id}`, { state: { listing } })}
                                >
                                  View Details
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
              </>
            );
          })()}

          {/* Pagination Controls */}
          {(() => {
            const filteredCount = listings.filter(listing => {
              const dateMatch = !filterDate || (listing.createdAt && 
                new Date(listing.createdAt).toISOString().split('T')[0] === filterDate);
              const propertyMatch = !filterProperty || 
                listing.address?.toLowerCase().includes(filterProperty.toLowerCase()) ||
                listing.city?.toLowerCase().includes(filterProperty.toLowerCase()) ||
                listing.zipCode?.includes(filterProperty);
              const agentMatch = !filterAgent || 
                currentUser?.email?.toLowerCase().includes(filterAgent.toLowerCase());
              return dateMatch && propertyMatch && agentMatch;
            }).length;
            const totalPages = Math.ceil(filteredCount / itemsPerPage);
            
            if (totalPages <= 1) return null;
            
            return (
              <div className="pagination-controls" style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                gap: '15px', 
                marginTop: '20px', 
                padding: '15px' 
              }}>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  style={{ 
                    padding: '8px 16px', 
                    background: currentPage === 1 ? '#ccc' : '#007bff', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px', 
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer' 
                  }}
                >
                  Previous
                </button>
                <span style={{ fontSize: '14px', fontWeight: '500' }}>
                  Page {currentPage} of {totalPages} ({filteredCount} listings)
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage >= totalPages}
                  style={{ 
                    padding: '8px 16px', 
                    background: currentPage >= totalPages ? '#ccc' : '#007bff', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px', 
                    cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer' 
                  }}
                >
                  Next
                </button>
              </div>
            );
          })()}
        </div>
      )}

      {/* Recommendations Modal */}
      {showRecommendations && (
        <div className="modal-overlay" onClick={() => setShowRecommendations(false)}>
          <div className="recommendations-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>💡 Recommendations to Improve Offer Score</h3>
              <button
                className="modal-close"
                onClick={() => setShowRecommendations(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              {selectedOffer && (
                <>
                  <div className="current-score-display">
                    <span>Current PTC Score:</span>
                    <div className={getScoreBadgeClass(selectedOffer.ptcScore ?? undefined)}>
                      {formatScore(selectedOffer.ptcScore ?? undefined)}
                    </div>
                  </div>

                  <div className="recommendation-section">
                    <h4>Recommendation</h4>
                    {selectedOffer.ptcInterpretation && (
                      <div className="interpretation" style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
                        <strong>Assessment:</strong> {selectedOffer.ptcInterpretation}
                      </div>
                    )}
                    {selectedOffer.ptcRecommendation && (
                      <div className="recommendation-text" style={{ padding: '12px', backgroundColor: '#e8f4f8', borderRadius: '6px', borderLeft: '4px solid #007bff' }}>
                        <strong>Action:</strong> {selectedOffer.ptcRecommendation}
                      </div>
                    )}
                    
                    {selectedOffer.ptcBreakdown && (
                      <div className="score-breakdown" style={{ marginTop: '20px' }}>
                        <h5 style={{ marginBottom: '12px' }}>Score Breakdown</h5>
                        <div className="breakdown-grid" style={{ display: 'grid', gap: '8px' }}>
                          {Object.entries(selectedOffer.ptcBreakdown).map(([key, data]: [string, any]) => {
                            if (typeof data === 'object' && data.score !== undefined) {
                              return (
                                <div key={key} className="breakdown-item" style={{ 
                                  display: 'grid', 
                                  gridTemplateColumns: '180px 1fr 60px', 
                                  gap: '12px', 
                                  padding: '8px 12px', 
                                  backgroundColor: '#f9f9f9', 
                                  borderRadius: '4px',
                                  alignItems: 'center'
                                }}>
                                  <span className="breakdown-label" style={{ fontWeight: '500', fontSize: '14px' }}>
                                    {key.replace(/([A-Z])/g, ' $1').trim()}:
                                  </span>
                                  <span className="breakdown-value" style={{ fontSize: '13px', color: '#555' }}>
                                    {data.value}
                                  </span>
                                  <span className="breakdown-score" style={{ 
                                    fontWeight: 'bold', 
                                    fontSize: '14px',
                                    color: data.score >= 7 ? '#28a745' : data.score >= 4 ? '#ffc107' : '#dc3545',
                                    textAlign: 'right'
                                  }}>
                                    {data.score.toFixed(1)}/10
                                  </span>
                                </div>
                              );
                            }
                            return null;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className="modal-footer-note" style={{ marginTop: '20px', padding: '12px', backgroundColor: '#fff3cd', borderRadius: '6px' }}>
                <p><strong>Note:</strong> These recommendations are based on the PTC (Probability to Close) scoring matrix. Share with your client to strengthen their offer.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Offer Compare Modal (Listings page) ── */}
      {compareListingId && (() => {
        const sel = selectedOffersByListing[compareListingId] || new Set<string>();
        const compareOffers = (offersByListing[compareListingId] || [])
          .filter(o => o.id && sel.has(o.id))
          .slice(0, 4);
        const listingsMap = Object.fromEntries(
          listings.filter(l => l.id).map(l => [l.id!, l])
        );
        if (compareOffers.length < 2) return null;
        return (
          <OfferCompareModal
            offers={compareOffers}
            listings={listingsMap}
            onClose={() => setCompareListingId(null)}
          />
        );
      })()}
    </div>
  );
};

export default Listings;
